import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { useDataApi } from "@core/apis";
import type { TypedChartSeries } from "@core/apis/shared";
import { useMeterDataQuery } from "@core/apis/shared/hooks";
import { getEquivalentPowerAggregatePerSubstationOptions } from "@core/apis/shared/queries/resource.queries";

import { HeatPowerSeriesType } from "../Optimization/HeatPowerChartContainer";
import type { NonNullableDateRange } from "./useGraphControlState";

/**
 * Heat power chart data (measured heat power + equivalent power).
 * Shared across PropAI-web and Platform-web through ISharedDataApi.
 */
export function useHeatPowerData(
  substationId: string,
  dateRange: NonNullableDateRange,
  options?: { networkUid?: string }
) {
  const dataApi = useDataApi();

  const { data: rawMeterData, isLoading: meterDataLoading } = useMeterDataQuery(
    substationId,
    ["heat_energy"],
    dateRange.startDate,
    dateRange.endDate,
    { networkUid: options?.networkUid }
  );

  const { data: equivalentPowerData, isLoading: equivalentPowerDataLoading } = useQuery({
    ...getEquivalentPowerAggregatePerSubstationOptions(
      dataApi,
      substationId,
      dateRange.startDate,
      dateRange.endDate,
      options?.networkUid
    ),
    enabled: !!dataApi && !!substationId,
  });

  const heatPowerChartData = useMemo(() => {
    const result: TypedChartSeries<HeatPowerSeriesType>[] = [];

    if (rawMeterData && rawMeterData.length > 0) {
      const heatEnergyComponent = rawMeterData.find((c: any) => c.component === "heat_energy");
      if (heatEnergyComponent && heatEnergyComponent.data?.length > 0) {
        const sortedData = [...heatEnergyComponent.data].sort(
          (a, b) => new Date(a.datetime).getTime() - new Date(b.datetime).getTime()
        );
        result.push({
          type: HeatPowerSeriesType.MEASURED_HEAT_POWER,
          data: sortedData.map((point) => ({ datetime: point.datetime, value: point.value })),
        });
      }
    }

    if (equivalentPowerData && equivalentPowerData.length > 0) {
      const epData = equivalentPowerData[0]?.data;
      if (epData && epData.length > 0) {
        const sortedData = [...epData].sort(
          (a, b) => new Date(a.datetime).getTime() - new Date(b.datetime).getTime()
        );
        result.push({
          type: HeatPowerSeriesType.EQUIVALENT_POWER,
          data: sortedData.map((item) => ({ datetime: item.datetime, value: item.value })),
        });
      }
    }

    return result;
  }, [rawMeterData, equivalentPowerData]);

  const isLoading = meterDataLoading || equivalentPowerDataLoading;
  const isEmpty = !isLoading && heatPowerChartData.length === 0;

  return {
    data: { heatPowerChartData },
    isLoading,
    isEmpty,
  };
}
